import { type JSX, useCallback, useLayoutEffect } from 'react';

import { useAppDispatch, useAppSelector } from 'app/hooks';
import { useDrawerContext } from 'common/components/DrawerContext/drawerContext';
import { selectCurrentDrawerId, selectCurrentEntity, selectCurrentTenantId } from 'common/components/EditDrawer/editDrawerSlice';
import { setRows } from 'common/components/PalmDataGrid/palmDataGridSlice';
import {
    fetchUserProfilePackageAssignments,
    fetchUserProfilePackageAssignmentsWithLicenseInfo,
    FormattedPackageAssignment,
    PackageAssignmentThunkResponse,
    postPackageAssignment,
    PostPackageAssignmentThunkParams,
    putPackageAssignment,
    PutPackageAssignmentThunkParams
} from 'common/services/api/ProductPackages/PackageAssignments.thunk';
import { DrawerEntitiesNames, EditDrawerContentProps, Operations } from 'common/types/TypeDefs';

import {
    useCreateStateHandler,
    useEditStateHandler,
} from './ProductPackageAssignmentDrawerContentStateHandlers';
import { PackageAssignmentPage } from './productPackageAssignmentTypeDefs';
import { PackageAssignmentDrawerProperties } from './productPackageAssignmentTypeDefs';

function usePackageAssignmentDrawer(
    drawerProps: PackageAssignmentDrawerProperties,
    editingItemInfo?: EditDrawerContentProps<FormattedPackageAssignment>['editingItemInfo']
) {
    const dispatch = useAppDispatch();

    const drawerContext = useDrawerContext<
        FormattedPackageAssignment,
        PackageAssignmentDrawerProperties,
        PostPackageAssignmentThunkParams,
        PackageAssignmentThunkResponse,
        PutPackageAssignmentThunkParams,
        PackageAssignmentThunkResponse
    >('Product Package Assignment', drawerProps, editingItemInfo);

    const createState = useCreateStateHandler(drawerProps);
    const editState = useEditStateHandler(
        drawerProps,
        editingItemInfo?.editingRow as FormattedPackageAssignment | undefined
    );

    const currentId = useAppSelector(selectCurrentDrawerId);

    useLayoutEffect(() => {
        if (drawerContext.action === Operations.Create) {
            createState.onOpenChange();
        } else {
            editState.onOpenChange(drawerContext.isOpenDrawer);
        }
    }, [drawerContext.isOpenDrawer, currentId]);

    const refreshUserProfileGrid = useCallback(() => {
        if (drawerProps.page !== PackageAssignmentPage.UserProfile) return;
        if (drawerProps.currentUserProfileTenant?.isLicenseRequired) {
            dispatch(fetchUserProfilePackageAssignmentsWithLicenseInfo({
                currentUserProfileTenant: drawerProps.currentUserProfileTenant,
                userId: drawerProps.principalId!,
            }));
        } else {
            dispatch(fetchUserProfilePackageAssignments({
                tenantId: drawerProps.tenantId,
                userId: drawerProps.principalId!,
            }));
        }
    }, [drawerProps, dispatch]);

    const createSuccessHandler = useCallback((result: PackageAssignmentThunkResponse) => {
        drawerContext.createSuccessHandler(result);
        if (drawerProps.page === PackageAssignmentPage.UserProfile) {
            refreshUserProfileGrid();
        } else {
            dispatch(setRows(result.data));
        }
    }, [drawerContext, dispatch, drawerProps.page, refreshUserProfileGrid]);

    const editSuccessHandler = useCallback((result: PackageAssignmentThunkResponse) => {
        drawerContext.editSuccessHandler(result);
        if (drawerProps.page === PackageAssignmentPage.UserProfile) {
            refreshUserProfileGrid();
        } else {
            dispatch(setRows(result.data));
        }
    }, [drawerContext, dispatch, drawerProps.page, refreshUserProfileGrid]);

    const getPostOptions = useCallback((): PostPackageAssignmentThunkParams => {
        return createState.getOptions() as PostPackageAssignmentThunkParams;
    }, [createState]);

    const getPutOptions = useCallback((): PutPackageAssignmentThunkParams => {
        return editState.getOptions() as PutPackageAssignmentThunkParams;
    }, [editState]);

    const postCallback = useCallback(() => {
        drawerContext.postCallback(postPackageAssignment, getPostOptions, createSuccessHandler);
    }, [drawerContext, getPostOptions, createSuccessHandler]);

    const putCallback = useCallback(() => {
        drawerContext.putCallback(putPackageAssignment, getPutOptions, editSuccessHandler);
    }, [drawerContext, getPutOptions, editSuccessHandler]);

    const getCreateContent = useCallback(() => {
        return createState.getContent(postCallback, drawerContext.processing);
    }, [createState, postCallback, drawerContext.processing]);

    const getEditContent = useCallback(() => {
        return editState.getContent(putCallback, drawerContext.processing);
    }, [editState, putCallback, drawerContext.processing]);

    return {
        ...drawerContext,
        getCreateContent,
        getEditContent,
    };
}

export function GetPackageAssignmentDrawerContent(drawerProps: PackageAssignmentDrawerProperties) {
    return PackageAssignmentDrawerContent.bind(null, drawerProps);
}

export function PackageAssignmentDrawerContent(
    drawerProps: PackageAssignmentDrawerProperties,
    editProps: EditDrawerContentProps<FormattedPackageAssignment>
): JSX.Element {
    const currentEntity = useAppSelector(selectCurrentEntity);
    const currentTenantId = useAppSelector(selectCurrentTenantId);
    const drawerState = usePackageAssignmentDrawer(drawerProps, editProps.editingItemInfo);

    if (drawerProps.page === PackageAssignmentPage.UserProfile) {
        if (currentEntity !== DrawerEntitiesNames.UserProfilePPA || currentTenantId !== editProps.tenant?.id) {
            return <></>;
        }
    }

    if (drawerState.action === Operations.Create) {
        return drawerState.getCreateContent();
    } else if (drawerState.action === Operations.Edit) {
        return drawerState.getEditContent();
    }

    return <></>;
}
